import type { ConversationMessage } from '@/domains/jobs/types/workOrder';
import { useJobConversations } from './useJobConversations';
import { useJobDetail } from './useJobDetail';

export interface JobData {
  id: string;
  title: string;
  description: string;
  client: string;
  siteAddress: string;
  address: string;
  priority: string;
  status: string;
  assignee?: string;
  dueDate?: string;
  requiredBy?: string;
  createdDate?: string;
  estimatedHours?: number;
  tags?: string[];
  category?: string;
  milestones?: Record<string, boolean>;
  tasks?: Array<{
    id: number;
    name: string;
    status: string;
    orderNumber?: number;
    isCategoryTask?: boolean;
  }>;
  formResponses?: unknown;
  capturedImages?: unknown;
  notes?: unknown;
  validated?: boolean;
  hasResponse?: boolean;
  responseDate?: string;
  responseMessage?: string;
  hasBeenResent?: boolean;
  resentDate?: string;
  resentInformation?: string;
  duration?: number;
  coordinates?: string;
  contractName?: string;
  externalId?: string | null;
  mirPendingDirection?: 'inbound' | 'outbound' | null;
  name?: string | null;
  jobStatusName?: string | null;
  permitRequired?: boolean;
  reinstatementRequired?: boolean;
  permitStatusId?: number | null;
  permitStatusName?: string | null;
}

export function useJobData(jobId: string) {
  const numericId = Number(jobId);
  const { data: apiData, isLoading, isError } = useJobDetail(numericId);
  const { data: conversationsData } = useJobConversations(numericId);

  const conversationThread: ConversationMessage[] = (conversationsData ?? []).map((msg) => ({
    id: String(msg.id),
    type: msg.type === 'accepted' || msg.type === 'other' ? 'resent' : msg.type,
    sender: msg.sender,
    timestamp: msg.timestamp,
    message: msg.message,
    createdBy: msg.createdBy,
    eventType: msg.eventType,
  }));

  let jobData: JobData;
  let jobStatus:
    | 'new'
    | 'more-info-requested'
    | 'rejected'
    | 'accepted'
    | 'completed'
    | 'validated'
    | null = null;

  if (apiData) {
    const statusName = apiData.work_order_status?.toLowerCase() ?? '';

    if (statusName === 'validated') jobStatus = 'validated';
    else if (statusName === 'completed' || statusName === 'complete') jobStatus = 'completed';
    else if (statusName === 'accepted' || statusName === 'in progress') jobStatus = 'accepted';
    else if (statusName === 'rejected') jobStatus = 'rejected';
    else if (statusName === 'more info requested' || statusName === 'more-info-requested')
      jobStatus = 'more-info-requested';
    else jobStatus = 'new';

    jobData = {
      id: String(apiData.id),
      title: apiData.name ?? 'Untitled Job',
      description: apiData.description ?? '',
      client: apiData.client_name ?? 'Unknown Client',
      siteAddress: apiData.address ?? '',
      address: [apiData.address, apiData.town, apiData.pincode].filter(Boolean).join(', '),
      priority: apiData.priority_name ?? apiData.priority ?? 'medium',
      status: apiData.work_order_status ?? 'unknown',
      assignee: apiData.assigned_to_name ?? undefined,
      dueDate: apiData.req_completion_date ?? undefined,
      requiredBy: apiData.req_completion_date ?? undefined,
      createdDate: apiData.created_time ?? undefined,
      tags: apiData.tags ? (apiData.tags as string[]) : undefined,
      category: apiData.job_category_name ?? undefined,
      milestones: apiData.milestones,
      tasks: apiData.tasks?.map((t) => ({
        id: t.id,
        name: t.name ?? 'Unnamed Task',
        status: String(t.status ?? 0),
        orderNumber: t.orderNumber ?? undefined,
        isCategoryTask: t.isCategoryTask ?? false,
      })),
      validated: apiData.validated,
      duration: apiData.duration ?? undefined,
      coordinates: apiData.coordinates ?? undefined,
      contractName: apiData.contract_name ?? undefined,
      notes: apiData.notes ?? undefined,
      externalId: apiData.external_id ?? null,
      mirPendingDirection: apiData.mir_pending_direction ?? null,
      name: apiData.name ?? null,
      jobStatusName: apiData.job_status_name ?? null,
      permitRequired: apiData.permit_required,
      reinstatementRequired: apiData.reinstatement_required,
      permitStatusId: apiData.permit_status_id ?? null,
      permitStatusName: apiData.permit_status_name ?? null,
      hasResponse: conversationThread.some((m) => m.type === 'response'),
    };
  } else {
    jobData = {
      id: jobId,
      title: isLoading ? 'Loading...' : 'Job Not Found',
      description: isLoading ? '' : 'This job could not be found in the system.',
      client: 'Unknown',
      siteAddress: 'Unknown',
      address: 'Unknown',
      priority: 'medium',
      status: 'pending',
    };
  }

  return { jobData, jobStatus, conversationThread, isLoading, isError };
}
